import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Award, Flower2, Globe, Mountain, Users } from "lucide-react";
import { STATISTICS } from "../../data/statistics";
import { useLanguage } from "../../context/LanguageContext";
import { useCountUp } from "../../hooks/useCountUp";
import { fadeUp, viewport } from "../../lib/animations";

const ICONS = { Mountain, Flower2, Users, Globe, Award };

function Stat({ stat, start, locale, localize }) {
  const Icon = ICONS[stat.icon] || Award;
  const count = useCountUp(stat.value, { start, duration: 2200 });
  const display = stat.isText
    ? localize(stat, "text")
    : `${count.toLocaleString(locale)}${stat.suffix}`;

  return (
    <motion.div
      variants={fadeUp}
      className="text-center md:text-left"
      data-testid={`stat-${stat.id}`}
    >
      <Icon
        size={26}
        strokeWidth={1.2}
        className="mx-auto mb-4 text-[#C4A46B] md:mx-0"
      />
      <div className="font-serif text-5xl leading-none tabular-nums text-[#F6F2EC] md:text-6xl">
        {display}
      </div>
      <div className="mt-3 font-sans text-[13px] uppercase tracking-[0.14em] text-[#F6F2EC]">
        {localize(stat, "label")}
      </div>
      <div className="mt-1 text-[13px] text-[#F6F2EC]/55">
        {localize(stat, "desc")}
      </div>
    </motion.div>
  );
}

export default function CorporateNumbers() {
  const { t, locale, localize } = useLanguage();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-15%" });

  return (
    <section
      ref={ref}
      className="bg-black py-24 md:py-32"
      data-testid="numbers-section"
    >
      <div className="container-tessa">
        <motion.h2
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={fadeUp}
          className="mb-16 max-w-3xl font-serif text-4xl leading-[1.05] text-[#F6F2EC] md:text-5xl lg:text-6xl"
        >
          {t("numbers.title1")}
          <br />
          <span className="italic text-[#D9A9AE]">{t("numbers.title2")}</span>
        </motion.h2>

        <motion.div
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={{ visible: { transition: { staggerChildren: 0.12 } } }}
          className="grid grid-cols-2 gap-x-6 gap-y-14 md:grid-cols-3 lg:grid-cols-5"
        >
          {STATISTICS.map((statistic) => (
            <Stat
              key={statistic.id}
              stat={statistic}
              start={inView}
              locale={locale}
              localize={localize}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
}
